/**
 * Engine singleton — lifts the AudioEngine OUT of any single React component
 * so it survives SPA route navigation between `/` (studio) and `/plugins`
 * (marketplace). Without this, the React Router unmounts ELPMStudioDAW on
 * route change, the unmount tears down the engine, and the marketplace
 * cannot dispatch `engine.loadPluginToChannel(...)` because window.__elpmEngine
 * is `undefined`.
 *
 * Lifecycle
 *   - Lazy-created on first `getEngine()` call (client-side only)
 *   - Cached on `window.__elpmEngine` for cross-route access + debugging
 *   - Never destroyed for the lifetime of the SPA tab
 *
 * Why a function call and not just `export default new AudioEngine(...)`?
 *   - AudioEngine's ctor needs `window.AudioContext`, which is undefined on
 *     SSR builds. Lazy creation defers the work until a component actually
 *     mounts and asks for the engine.
 */

import { AudioEngine } from "@/lib/audioEngine";

const TOTAL_CHANNELS = 64;

export function getEngine() {
  if (typeof window === "undefined") return null;
  if (window.__elpmEngine) return window.__elpmEngine;
  const engine = new AudioEngine(TOTAL_CHANNELS);
  window.__elpmEngine = engine;
  return engine;
}

/** Test-only helper — DO NOT call from app code. Tears down the cached engine
 *  so a subsequent getEngine() call rebuilds. Useful when a test wants a
 *  clean AudioContext state. */
export function __resetEngine() {
  try { window.__elpmEngine?.disposeAll?.(); } catch (e) { void e; }
  if (typeof window !== "undefined") delete window.__elpmEngine;
}
